import { useState, useEffect, useRef, useMemo } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { FaEdit, FaTimes } from "react-icons/fa";
import { toast } from 'react-toastify'
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import dateFormat from "dateformat";
import Server from "../../Shared/Server"
import Spinner from '../../Shared/Spinner'
import { InputField } from "../../Shared/InputField"

import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const MotorReport = () => {
    const [reports, setMotorReport] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [show, setShow] = useState(false);
    const [errors, setErrors] = useState(false);
    const [publicID, setPublicID] = useState()
    const [title, setTitle] = useState('')
    const gridRef = useRef();

    const [formData, setFormData] = useState({
        fdate: new Date().toISOString().slice(0, 10),
        tdate: new Date().toISOString().slice(0, 10),
    })
    const {fdate, tdate} = formData

    const navigate = useNavigate()
    const location = useLocation()

    useEffect(() => {
        if (location.state){
            const {payload} = location.state
            const fdate = dateFormat(new Date(payload.year, payload.month-1, payload.day), "yyyy-mm-dd");
            const tdate = dateFormat(new Date(payload.year, payload.month-1, payload.day), "yyyy-mm-dd");
            setTitle(`Motor Daily Report From ${fdate} To ${tdate}`)
            setFormData((prev) => ({
                ...prev,
                fdate: fdate,
                tdate: tdate
            }))
            fetchData({fdate: fdate, tdate: tdate})
        }else{
            setTitle(`Motor Daily Report From ${fdate} To ${tdate}`)
            const payload = {fdate, tdate}
            fetchData(payload)
        }

    }, [])

    const handleClose = () => {
        if (isDeleting) return
        setShow(false)
    }
    const handleShow = (public_id) => {
        setShow(true)
        setPublicID(public_id)
    };

    const handleDelete = async (public_id) => {
        setIsDeleting(true)
        try {
            const response = await Server.delete(`/motor-reports/${public_id}`)
            if (response.status === 200){
                toast.success(response.data.detail)
                setMotorReport(prev => prev.filter((report) => report.public_id !== public_id))
            }else{
                toast.error(response?.data?.detail || 'Failed to delete motor report')
            }
        } catch (error) {
            if(error?.response?.status === 401){
                navigate('/login')
            }else if (error?.response?.status === 403){
                toast.error(error?.response?.data?.detail || 'Forbidden');
                navigate('/');
            }else{
                toast.error(error?.response?.data?.detail || 'Failed to delete motor report')
            }
        } finally {
            setIsDeleting(false)
            setShow(false)
        }
      }

    const onChange = (e) => {
        setFormData((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value
        }))

    }

    const onSubmit = (e) => {
        e.preventDefault()
        fetchData({fdate, tdate})
    }

    const fetchData = async (payload) => {
        setIsLoading(true)
        try {
            const response = await Server.get(`/motor-reports/?fdate=${payload.fdate}&tdate=${payload.tdate}`)
            setMotorReport(response?.data)
            setIsLoading(false)
        } catch (error) {
            setIsLoading(false)
            if(error?.response?.status === 401){
                localStorage.removeItem('user')
                navigate('/login')
            }else if (error?.response?.status === 422) {
                setErrors({})
                error.response?.data?.detail.map((err) => (
                    setErrors((prevState) => ({
                        ...prevState,
                        [err.loc[1]]: err.msg
                    }))
                ))
            }else if (error?.response?.status === 403){
                toast.error(error?.response?.data?.detail || 'Forbidden');
                navigate('/');
            }else {
                toast.error(error?.response?.data?.detail || 'Failed to load motor reports');
            }
        }
    }

    const defaultColDef = useMemo(() => {
        return {
            filterParams: {
                buttons: []
            }
        };
    }, []);

    const columnDefs = useMemo(() => [
        {
            headerName: "#",
            filter: false,
            maxWidth: 70,
            sortable: true,
            valueFormatter: function (params) {
            if (isNaN(params.node.id)) {
                return "";
            }
            return `${Number(params.node.rowIndex) + 1}`;
            },
        },

        {
            headerName: "Date",
            field: "date",
            sortable: true,
            filter: 'agDateColumnFilter',
            filterParams: {
                comparator: (filterDate, cellValue) => {
                  const cellDate = new Date(cellValue);
                  cellDate.setHours(0, 0, 0, 0);
                  filterDate.setHours(0, 0, 0, 0);

                  if (cellDate < filterDate) return -1;
                  if (cellDate > filterDate) return 1;
                  return 0;
                },
            },
        },
        {
            headerName: "Motor",
            field: "motor",
            sortable: true,
            filter: true,
        },
        {
            headerName: "User",
            field: "user",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Previous Reading",
            field: "previous_reading",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Current Reading",
            field: "current_reading",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Difference",
            field: "difference",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Fuel",
            field: "fuel",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Engine Condition",
            field: "engine_condition",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Notes",
            field: "notes",
            sortable: true,
            filter: true,
        },
        {
            headerName: "GPS Latitude",
            field: "gps_latitude",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },{
            headerName: "GPS Longitude",
            field: "gps_longitude",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Actions",
            pinned: 'left',
            minWidth: 120,
            sortable: false,
            filter: false,
            cellRenderer: (e) => {
                if (!e.data) return null;
                return (
                    <div className="d-flex gap-1">
                        <Link className="btn btn-outline-info" to="/update-motor-report" state={{ data: e.data }} title="Edit motor report"><FaEdit/></Link>
                        <button className="btn btn-outline-danger" onClick={()=> handleShow(e.data.public_id)} title="Cancel motor report"><FaTimes/></button>
                    </div>
                )
            },
        },
    ], []);

    const onFirstDataRendered = (params) => {
        params.api.autoSizeAllColumns();
    }

    const onGridSizeChanged = (params) => {
        params.api.autoSizeAllColumns();
    }

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
        <form onSubmit={onSubmit}>
            <div className="row">
                <div className="col-md-2">
                    <InputField
                        name="fdate"
                        type="date"
                        required="required"
                        value={fdate}
                        onChange={onChange}
                        error={errors.fdate}
                    />
                </div>
                <div className="col-md-2">
                    <InputField
                        name="tdate"
                        type="date"
                        required="required"
                        value={tdate}
                        onChange={onChange}
                        error={errors.tdate}
                    />
                </div>
                <div className="col-md-4">
                    <input type="submit" className="btn btn-info mt-1" value="Apply Filter" style={{float: 'left'}}/>
                </div>
                <div className="col-md-4">
                    <Link to='/add-motor-report' className="btn btn-outline-info mb-2" style={{float: 'right'}}>Add Report</Link>
                </div>
            </div>
            <br/>
        </form>
        <div className="card">
        <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                <h3 className="card-title">{title}</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            defaultColDef={defaultColDef}
                            onFirstDataRendered={onFirstDataRendered}
                            onGridSizeChanged={onGridSizeChanged}
                            suppressMenuHide={true}

                            rowData={reports}
                            domLayout={"normal"}
                            rowSelection={"multiple"}

                            animateRows={true}
                            pagination={false}
                            resizable={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}


                            ref={gridRef}
                            groupIncludeTotalFooter={false}
                            groupIncludeFooter={true}
                            columnDefs={columnDefs}
                        ></AgGridReact>{" "}
                    </div>
                    </div>
                </div>
        </div>
        </div>

        <Modal show={show} onHide={handleClose} backdrop={isDeleting ? 'static' : true}>
        <Modal.Header closeButton={!isDeleting}>
          <Modal.Title>Delete Alert</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure?</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleClose} disabled={isDeleting}>
            No
          </Button>
          <Button variant="danger" onClick={() => handleDelete(publicID)} disabled={isDeleting}>
            {isDeleting ? 'Deleting\u2026' : 'Yes'}
          </Button>
        </Modal.Footer>
      </Modal>
      <br/>
    </>
  )
}

export default MotorReport